import _ from 'lodash';
import moment from 'moment';
import { createMinutelyHrChartData, filterHourlySummaryRanges } from './heartRateCharts';

/**
 * Summarises the heart rate over an event's minute index range. Averages the non-negative minutely averages inside the range, and for each extreme either takes the value already carried on the event together with its start index, or scans the range for the extreme and the index it occurs at.
 *
 * @param avgHrArray - Minutely average heart rates for the day.
 * @param maxHrArray - Minutely maximum heart rates for the day.
 * @param minHrArray - Minutely minimum heart rates for the day.
 * @param event - The event whose startIndex and stopIndex bound the range, and whose own maxHr and minHr win when present.
 * @returns An object carrying avgHr, maxHr, minHr and the indices of the extremes.
 */
export const calculateHrInfo = ({ avgHrArray, maxHrArray, minHrArray, event }: any) => {
  const selectedEventAvgHrs = _.filter(
    avgHrArray,
    (x, index) => index >= event.startIndex && index <= event.stopIndex,
  );
  const removedNegativeAvgHrArray = [...selectedEventAvgHrs];
  _.remove(removedNegativeAvgHrArray, (x) => x < 0);
  const avgHr = Math.round(_.sum(removedNegativeAvgHrArray) / removedNegativeAvgHrArray.length);

  let maxHr: any;
  let maxHrIndex;
  if (!_.isNil(event.maxHr)) {
    maxHr = event.maxHr;
    maxHrIndex = event.startIndex;
  } else {
    const selectedEventMaxHrs = _.filter(
      maxHrArray,
      (x, index) => index >= event.startIndex && index <= event.stopIndex,
    );
    const removedNegativeMaxHrArray = [...selectedEventMaxHrs];
    _.remove(removedNegativeMaxHrArray, (x) => x < 0);
    maxHr = _.max(removedNegativeMaxHrArray);
    maxHrIndex = _.findIndex(
      maxHrArray,
      (x, index) => index >= event.startIndex && index <= event.stopIndex && x === maxHr,
    );
  }

  let minHr: any;
  let minHrIndex;
  if (!_.isNil(event.minHr)) {
    minHr = event.minHr;
    minHrIndex = event.startIndex;
  } else {
    const selectedEventMinHrs = _.filter(
      minHrArray,
      (x, index) => index >= event.startIndex && index <= event.stopIndex,
    );
    const removedNegativeMinHrArray = [...selectedEventMinHrs];
    _.remove(removedNegativeMinHrArray, (x) => x < 0);
    minHr = _.min(removedNegativeMinHrArray);
    minHrIndex = _.findIndex(
      minHrArray,
      (x, index) => index >= event.startIndex && index <= event.stopIndex && x === minHr,
    );
  }

  return {
    avgHr,
    maxHr,
    minHr,
    maxHrIndex,
    minHrIndex,
  };
};

/**
 * Summarises the heart rate of a span using the calendar day it falls in as the index base. Filters the hourly summaries down to that day, rebuilds the minutely average, maximum and minimum series across it, converts the span's start and stop into minute offsets from midnight, and averages and finds the extremes over that range.
 *
 * @param ecgDataMap - The recorded hourly summaries, plus the channels and sampling frequency used to filter them.
 * @param start - Start of the span being summarised.
 * @param stop - Stop of the span being summarised.
 * @returns An object carrying avgHr, maxHr, minHr and the indices of the extremes.
 */
export const calculateHrInfoByDate = ({ ecgDataMap, start, stop }: any) => {
  const startDayMoment = moment(start).startOf('days');
  const stopDayMoment = moment(stop).endOf('days');
  const currentDateData: any[] = filterHourlySummaryRanges({
    summaries: ecgDataMap,
    start: startDayMoment,
    stop: stopDayMoment,
    channels: ecgDataMap.channels,
    samplingFrequency: ecgDataMap.samplingFrequency,
    willAddDataRanges: false,
  } as any);
  const filteredAvgHrArray: any[] = [];
  const filteredMaxHrArray: any[] = [];
  const filteredMinHrArray: any[] = [];
  currentDateData.forEach((item) => {
    filteredAvgHrArray.push(item.avgHrs);
    filteredMaxHrArray.push(item.maxHrs);
    filteredMinHrArray.push(item.minHrs);
  });
  const [avgHrArray, maxHrArray, minHrArray] = createMinutelyHrChartData({
    hourlySummaries: currentDateData,
    minutelyHrs: filteredAvgHrArray,
    start: startDayMoment,
    stop: stopDayMoment,
    maxMinutelyHrs: filteredMaxHrArray,
    minMinutelyHrs: filteredMinHrArray,
    isEditReportPage: true,
  });

  const firstEpoch = startDayMoment.valueOf();
  const roundStartMoment = moment(start).startOf('minutes');
  const roundStopMoment = moment(stop).startOf('minutes');

  const event = {
    start,
    stop,
    startIndex: (roundStartMoment.valueOf() - firstEpoch) / 1000 / 60,
    stopIndex: (roundStopMoment.valueOf() - firstEpoch) / 1000 / 60,
  };
  const hrInfo = calculateHrInfo({
    avgHrArray,
    maxHrArray,
    minHrArray,
    event,
  });

  return hrInfo;
};
